import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Use environment variable for API base URL (configured in .env file)
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || "/api/v1/",
  prepareHeaders: (headers, { getState }) => {
    const token = getState()?.auth?.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    
    // Globally attach the user's active language to every API request
    const lang = localStorage.getItem("i18nextLng") || "en";
    headers.set("Accept-Language", lang);
    
    return headers;
  },
  responseHandler: async (response) => {
    const res = await response.json();
    return res;
  },
});

export const clientApi = createApi({
  reducerPath: "api",
  baseQuery,
  tagTypes: [
    "Places",
    "Channels",
    "users",
    "routes",
    "postsComments",
    "stages",
    "reports",
    "notifications",
    "UserNotifications",
    "MapMessages",
    "PostComments",
    "reviews",
    "AppReviews",
  ],
  endpoints: (builder) => ({
    createUser: builder.mutation({
      query: (userData) => ({
        url: "/user/register",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["users"],
    }),
    userLogin: builder.mutation({
      query: (userData) => ({
        url: "/auth/login",
        method: "POST",
        body: userData,
      }),
    }),
    verifyForgotPasswordOtp: builder.mutation({
      query: (userData) => ({
        url: "/auth/forgot-password/verify-otp",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["users"],
    }),
    resetPassword: builder.mutation({
      query: (userData) => ({
        url: "/auth/forgot-password/reset",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["users"],
    }),
    createRoutes: builder.mutation({
      query: (userData) => ({
        url: "/admin/create-routes",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["routes"],
    }),
    createPlaces: builder.mutation({
      query: (userData) => ({
        url: "/admin/create-place",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["Places"],
    }),
    createStages: builder.mutation({
      query: (userData) => ({
        url: "/userStage/create-stage",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["Places"],
    }),
    getAllPlaces: builder.query({
      query: ({ page, limit, search }) =>
        "/admin/get-all-place?page=" +
        page +
        "&limit=" +
        limit +
        "&search=" +
        search,
      providesTags: ["Places"],
    }),
    getAllStages: builder.query({
      query: () => "/stages/on-user-id",
      providesTags: ["stages"],
    }),
    getPlaceById: builder.query({
      query: (id) => `/admin/get-place/${id}`,
      providesTags: ["Places"],
    }),
    getStageById: builder.query({
      query: (id) => `/userStage/get-stage/${id}`,
      providesTags: ["Places"],
    }),
    updatePlace: builder.mutation({
      query: (userData) => ({
        url: "/admin/update-place",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["Places"],
    }),
    updateStage: builder.mutation({
      query: (userData) => ({
        url: "/userStage/update-stage",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["Places"],
    }),
    deletePlaces: builder.mutation({
      query: (id) => ({
        url: `/admin/delete-place/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Places"],
    }),
    deleteManyPlaces: builder.mutation({
      query: (data) => ({
        url: `/places`,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["Places"],
    }),
    deleteStages: builder.mutation({
      query: (id) => ({
        url: `/userStage/delete-stage/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Places"],
    }),
    deleteUsers: builder.mutation({
      query: (id) => ({
        url: `/user/delete-user/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["users"],
    }),
    createChannel: builder.mutation({
      query: (userData) => ({
        url: "/channel/create-channel",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["Channels"],
    }),
    getAllChannels: builder.query({
      query: () => `/channel/get-all-channel`,
      providesTags: ["Channels"],
    }),
    getAllAdminChannels: builder.query({
      query: ({ page, limit, search }) =>
        `/channel/get-all-admin-channel?page=${page}&limit=${limit}&search=${search}`,
      providesTags: ["Channels"],
    }),
    getChannelById: builder.query({
      query: (id) => `/channel/get-channel/${id}`,
      providesTags: ["Channels"],
    }),
    updateChannel: builder.mutation({
      query: (userData) => ({
        url: "/channel/update-channel",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["Channels"],
    }),
    deleteChannel: builder.mutation({
      query: (id) => ({
        url: `/channel/delete-channel/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Channels"],
    }),
    sendOtpWithEmail: builder.mutation({
      query: (userData) => ({
        url: "/auth/sendotp-email",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["users"],
    }),
    verifyOtp: builder.mutation({
      query: (userData) => ({
        url: "/auth/verify-otp",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["users"],
    }),
    resendOtp: builder.mutation({
      query: (userData) => ({
        url: "/auth/resend-otp",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["users"],
    }),
    getAllUsers: builder.query({
      query: ({ page, limit, search }) =>
        `/user/get-all-users?page=${page}&limit=${limit}&search=${search}`,
      providesTags: ["users"],
    }),
    getPreSignedUrl: builder.mutation({
      query: (userData) => ({
        url: "/helper/getPreSignedUrl",
        method: "POST",
        body: userData,
      }),
    }),
    getAllRoutes: builder.query({
      query: () => "/admin/getAll-routes",
      providesTags: ["routes"],
    }),
    getDashboardData: builder.query({
      query: (year) => `/admin/dashboard?year=${year}`,
    }),
    getRoutesById: builder.query({
      query: (id) => {
        return `/admin/get-route/${id}`;
      },
      providesTags: ["routes"],
    }),
    updateRoute: builder.mutation({
      query: (userData) => ({
        url: "/admin/update-routes",
        method: "PATCH",
        body: userData,
      }),
      invalidatesTags: ["routes"],
    }),
    deleteRoute: builder.mutation({
      query: (id) => ({
        url: `/admin/delete-routes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["routes"],
    }),
    getPlacesWithRoutes: builder.query({
      query: ({ routeId, selectedStageId }) => {
        let url = `/admin/getPlacesWithRoutes?routeId=${routeId}`;
        if (selectedStageId) {
          url += `&stageId=${selectedStageId}`;
        }
        return url;
      },
      providesTags: ["Places"],
    }),
    getUser: builder.query({
      query: ({ id } = {}) => `/user/get-user?id=${id}`,
      providesTags: ["users"],
    }),
    updateUsers: builder.mutation({
      query: (userData) => ({
        url: "/user/update-users",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["users"],
    }),
    applyForVolunteer: builder.mutation({
      query: () => ({
        url: "/user/apply-volunteer",
        method: "PATCH",
      }),
      invalidatesTags: ["users"],
    }),
    createComment: builder.mutation({
      query: (body) => ({
        url: "/channel/comment",
        method: "POST",
        body,
      }),
      invalidatesTags: ["postsComments"],
    }),
    getAllComments: builder.query({
      query: ({ channelId, page = 1, limit = 10 }) => `/channel/comment/${channelId}?page=${page}&limit=${limit}`,
      providesTags: ["postsComments"],
    }),
    createReply: builder.mutation({
      query: (body) => ({
        url: "/channel/reply",
        method: "POST",
        body,
      }),
      invalidatesTags: ["postsComments"],
    }),
    getAllReplyes: builder.query({
      query: () => `/channel/reply`,
      providesTags: ["postsComments"],
    }),
    getSettings: builder.query({
      query: () => "/user/get-user", 
      providesTags: ["users"],
    }),
    updateSettings: builder.mutation({
      query: (userData) => ({
        url: "/user/update-settings",
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["users"],
    }),
    getAllReviews: builder.query({
      query: ({ placeId }) => `/reviews?placeId=${placeId}`,
      providesTags: ["reviews"],
    }),
    createReview: builder.mutation({
      query: (body) => ({
        url: "/reviews",
        method: "POST",
        body,
      }),
      invalidatesTags: ["reviews"],
    }),
    getNotifications: builder.query({
      query: ({ page, limit }) =>
        `/comments/notifications?page=${page}&limit=${limit}`,
      providesTags: ["notifications"],
    }),
    createMapMessage: builder.mutation({
      query: (body) => ({
        url: "/map-messages",
        method: "POST",
        body,
      }),
      invalidatesTags: ["MapMessages"], 
    }),
    
    getMapMessages: builder.query({
      query: ({ routeId, userId }) => ({
        url: `/map-messages`,
        params: { routeId, userId }, 
      }),
      providesTags: ["MapMessages"], 
    }),
    
    updateCount: builder.mutation({
      query: (userData) => ({
        url: "/map-messages/update-count", 
        method: "POST",
        body: userData,
      }),
      invalidatesTags: ["MapMessages"], 
    }),
    fetchAllNotification: builder.query({
      query: () => "/user/notifications",
      providesTags: ["UserNotifications"],
    }),
    deleteNotification: builder.mutation({
      query: () => ({
        url: `/comments/notifications/clear`,
        method: "DELETE",
      }),
      invalidatesTags: ["notifications"],
    }),
    createStage: builder.mutation({
      query: (stageData) => {
        return {
          url: "/stages",
          method: "POST",
          body: stageData,
        };
      },
      invalidatesTags: ["stages"],
    }),
    getStagesOnRouteId: builder.query({
      query: (stageId) => `/stages/${stageId}`,
      providesTags: ["stages"],
    }),
    deleteStage: builder.mutation({
      query: (id) => ({
        url: `/stages/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["stages"],
    }),
    createPlace: builder.mutation({
      query: (placeData) => {
        return {
          url: "/places",
          method: "POST",
          body: placeData,
        };
      },
      invalidatesTags: ["Places"],
    }),
    editPlace: builder.mutation({
      query: ({ placeId, placeData }) => {
        return {
          url: `/places/${placeId}`,
          method: "PATCH",
          body: placeData,
        };
      },
      invalidatesTags: ["Places"],
    }),
    getPlacesOnRouteId: builder.query({
      query: (routeId) => `/places/route/${routeId}`,
      providesTags: ["Places"],
    }),
    getPlacesOnStageId: builder.query({
      query: (stageId) => `/places/stage/${stageId}`,
      providesTags: ["Places"],
    }),
    getSmartPlacesSearch: builder.query({
      query: ({ searchTerm, routeId, category }) => {
        // Build the URL dynamically based on what is provided
        let url = `/places/search?searchTerm=${encodeURIComponent(searchTerm)}`;
        if (routeId) url += `&routeId=${routeId}`;
        if (category) url += `&category=${category}`;
        
        return {
          url: url,
          method: "GET",
        };
      },
      // ✨ Keep the AI search results cached for 1 hour to prevent re-triggering on back-navigation!
      keepUnusedDataFor: 3600,
    }),
    deletePlace: builder.mutation({
      query: (placeId) => ({
        url: `/places/${placeId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Places"], 
    }),
    getFeaturedPlaces: builder.query({
      query: () => ({ url: "/places/featured", method: "GET" }),
    }),
    getMyPlaces: builder.query({
      query: () => ({ url: "/places/my-places", method: "GET" }),
    }),
    sharePrivatePlaces: builder.mutation({
      query: ({ placeId, selected }) => {
        return {
          url: `/places/${placeId}/share`,
          method: "PATCH",
          body: selected,
        };
      },
      invalidatesTags: ["Places"],
    }),
    getAllRoutesForDownload: builder.query({
      query: (params = {}) => ({
        url: "/routes/all-routes-for-download",
        params: params,
      }),
    }),
    getSingleRouteForDownload: builder.query({
      query: (routeId) => ({
        url: `/routes/single-route-for-download/${routeId}`,
      }),
    }),
    createReport: builder.mutation({
      query: (data) => {
        return {
          url: `/reports`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["reports", "Places", "postsComments"],
    }),
    getAllReports: builder.query({
      query: (params = {}) => ({
        url: "/reports",
        method: "GET",
        params: params,
      }),
      providesTags: ["reports", "Places", "postsComments"],
    }),
    getReportById: builder.query({
      query: (id) => ({
        url: `/reports/${id}`,
        method: "GET",
      }),
      providesTags: ["reports", "Places", "postsComments"],
    }),
    updateReport: builder.mutation({
      query: ({ id, data }) => {
        return {
          url: `/reports/${id}`,
          method: "PATCH",
          body: data,
        };
      },
      invalidatesTags: ["reports", "Places", "postsComments"],
    }),
    clearChat: builder.mutation({
      query: (conversationId) => {
        return {
          url: `/private-chat/clear-chat/${conversationId}`,
          method: "POST",
        };
      },
    }),
    getPrivateMessages: builder.query({
      query: (userId) => ({
        url: `/private-chat/private/${userId}`,
        method: "GET",
      }),
    }),
    getAllMapMessages: builder.query({
      query: ({ page = 1, search = "" }) => ({
        url: "/map-messages",
        method: "GET",
        params: {
          page,
          search,
        },
      }),
      providesTags: ["MapMessages"],
    }),
    deleteMapMessageOnId: builder.mutation({
      query: (mapMessageId) => {
        return {
          url: `/map-messages/${mapMessageId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["MapMessages"],
    }),
    getAllPostComments: builder.query({
      query: ({ page = 1, search = "" }) => ({
        url: "/post-comments",
        method: "GET",
        params: {
          page,
          search,
        },
      }),
      providesTags: ["PostComments"],
    }),
    deletePostCommentsOnId: builder.mutation({
      query: (mapMessageId) => {
        return {
          url: `/post-comments/${mapMessageId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["PostComments"],
    }),
    getAllReviewsAdmin: builder.query({
      query: ({ page = 1, search = "" }) => ({
        url: "/reviews/admin",
        method: "GET",
        params: {
          page,
          search,
        },
      }),
      providesTags: ["reviews"],
    }),
    deleteReviewOnReviewId: builder.mutation({
      query: (reviewId) => {
        return {
          url: `/reviews/${reviewId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["reviews"],
    }),
    createAppReview: builder.mutation({
      query: (data) => {
        return {
          url: `/app-review`,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: ["AppReviews"],
    }),
    getAllAppReviews: builder.query({
      query: ({ page = 1, search = "" }) => ({
        url: "/app-review",
        method: "GET",
        params: {
          page,
          search,
        },
      }),
      providesTags: ["AppReviews"],
    }),
    deleteAppReviewOnReviewId: builder.mutation({
      query: (reviewId) => {
        return {
          url: `/app-review/${reviewId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["AppReviews"],
    }),
    getPendingVolunteers: builder.query({
      query: ({ page = 1, search = "" }) => ({
        url: "/admin/pending-volunteers",
        method: "GET",
        params: {
          page,
          search,
        },
      }),
      providesTags: ["users"],
    }),
    updateVolunteerStatus: builder.mutation({
      query: (data) => ({
        url: "/admin/update-volunteer-status",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["users"],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useCreateMapMessageMutation,
  useGetMapMessagesQuery,
  useUpdateCountMutation,
  useDeleteNotificationMutation,
  useCreateUserMutation,
  useApplyForVolunteerMutation,
  useUserLoginMutation,
  useCreateRoutesMutation,
  useCreatePlacesMutation,
  useCreateStagesMutation,
  useGetAllPlacesQuery,
  useGetPlaceByIdQuery,
  useUpdatePlaceMutation,
  useDeletePlaceMutation,
  useDeletePlacesMutation,
  useDeleteManyPlacesMutation,
  useDeleteUsersMutation,
  useCreateChannelMutation,
  useGetAllChannelsQuery,
  useGetChannelByIdQuery,
  useUpdateChannelMutation,
  useDeleteChannelMutation,
  useVerifyOtpMutation,
  useSendOtpWithEmailMutation,
  useResendOtpMutation,
  useVerifyForgotPasswordOtpMutation,
  useResetPasswordMutation,
  useGetAllUsersQuery,
  useGetPreSignedUrlMutation,
  useGetAllRoutesQuery,
  useGetDashboardDataQuery,
  useGetRoutesByIdQuery,
  useUpdateRouteMutation,
  useDeleteRouteMutation,
  useGetPlacesWithRoutesQuery,
  useGetUserQuery,
  useUpdateUsersMutation,
  useCreateCommentMutation,
  useGetAllCommentsQuery,
  useCreateReplyMutation,
  useGetAllReplyesQuery,
  useGetAllAdminChannelsQuery,
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useGetAllReviewsQuery,
  useCreateReviewMutation,
  useGetAllStagesQuery,
  useDeleteStagesMutation,
  useGetStageByIdQuery,
  useDeleteStageMutation,
  useCreateStageMutation,
  useLazyGetStagesOnRouteIdQuery,
  useCreatePlaceMutation,
  useEditPlaceMutation,
  useGetPlacesOnRouteIdQuery,
  useGetPlacesOnStageIdQuery,
  useSharePrivatePlacesMutation,
  useLazyGetSmartPlacesSearchQuery,
  useGetSmartPlacesSearchQuery,
  useLazyGetAllRoutesForDownloadQuery,
  useGetAllRoutesForDownloadQuery,
  useLazyGetSingleRouteForDownloadQuery,
  useCreateReportMutation,
  useGetAllReportsQuery,
  useGetReportByIdQuery,
  useUpdateReportMutation,
  useLazyFetchAllNotificationQuery,
  useClearChatMutation,
  useLazyGetPrivateMessagesQuery,
  useGetAllMapMessagesQuery,
  useDeleteMapMessageOnIdMutation,
  useGetAllPostCommentsQuery,
  useDeletePostCommentsOnIdMutation,
  useGetAllReviewsAdminQuery,
  useDeleteReviewOnReviewIdMutation,
  useCreateAppReviewMutation,
  useGetAllAppReviewsQuery,
  useDeleteAppReviewOnReviewIdMutation,
  useGetFeaturedPlacesQuery,
  useGetMyPlacesQuery,
  useGetPendingVolunteersQuery,
  useUpdateVolunteerStatusMutation
} = clientApi;